import React from 'react'
import { Check, Crosshair } from 'lucide-react'

/**
 * Scroll the matching result card into view inside Puck's canvas iframe.
 * The picker lives in the parent sidebar; results render in the iframe, so
 * we find the frame whose document holds the tagged element.
 */
/** Scroll a tagged element into view inside Puck's canvas iframe + flash it.
 * The picker is in the parent sidebar; the canvas is an iframe, so we search
 * each frame's document for the tag. */
const scrollToTag = (attr: string, id: string) => {
  const frames = Array.from(document.querySelectorAll('iframe'))
  for (const f of frames) {
    let doc: Document | null = null
    try {
      doc = f.contentDocument
    } catch {
      doc = null
    }
    const el = doc?.querySelector(`[${attr}="${id}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      ;(el as HTMLElement).style.transition = 'outline 0.2s'
      ;(el as HTMLElement).style.outline = '3px solid #4363d8'
      setTimeout(() => {
        ;(el as HTMLElement).style.outline = ''
      }, 1200)
      return
    }
  }
}
const scrollToResult = (id: string) => scrollToTag('data-pq-result-id', id)
const scrollToAnswer = (id: string) => scrollToTag('data-pq-answer-id', id)

export type PQResultOption = {
  value: string
  label: string
  color: string
}

/**
 * Decision-tree answer→results picker (editor field). A labeled, color-coded
 * multi-select: each row is a result (identity dot + title), click toggles.
 * Colors match the badges on the result cards + the answer chips, so authors
 * can trace an answer to its results at a glance.
 */
export const PQResultPicker = ({
  options,
  value,
  onChange,
  selfId,
}: {
  options: PQResultOption[]
  value?: string[]
  onChange: (v: string[]) => void
  selfId?: string
}) => {
  const selected = value ?? []
  const has = (v: string) => selected.includes(v)
  const toggle = (v: string) =>
    onChange(has(v) ? selected.filter(x => x !== v) : [...selected, v])

  if (!options.length) {
    return (
      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)', padding: '6px 0' }}>
        Add Quiz Results first, then choose which ones this answer leads to.
      </div>
    )
  }

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'rgba(0,0,0,0.55)',
          marginBottom: 6,
          lineHeight: 1.4,
        }}
      >
        Which results does this answer lead to? Pick one or more (decision
        tree). <b>{selected.length}</b> selected.
        {selfId && (
          <>
            {' '}
            <span
              role="button"
              onClick={() => scrollToAnswer(selfId)}
              style={{
                color: '#4363d8',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Jump to this answer
            </span>
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 6,
          padding: 4,
        }}
      >
        {options.map(o => {
          const on = has(o.value)
          return (
            <div
              key={o.value}
              role="button"
              tabIndex={0}
              onClick={() => toggle(o.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') toggle(o.value)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                textAlign: 'left',
                padding: '6px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13,
                background: on ? `${o.color}1f` : 'transparent',
              }}
            >
              <span
                style={{
                  flex: '0 0 auto',
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  border: `2px solid ${o.color}`,
                  background: on ? o.color : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {on && <Check size={11} color="#fff" strokeWidth={3} />}
              </span>
              <span
                aria-hidden
                style={{
                  flex: '0 0 auto',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: o.color,
                }}
              />
              <span style={{ flex: 1 }}>{o.label}</span>
              <span
                role="button"
                title="Scroll to this result in the preview"
                onClick={e => {
                  e.stopPropagation()
                  scrollToResult(o.value)
                }}
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 2,
                  borderRadius: 4,
                  color: 'rgba(0,0,0,0.4)',
                }}
              >
                <Crosshair size={14} />
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
